// Widget „Status & Eigenschaften“ (Handoff §6.1): Status als Auswahlzeile mit 8-stufigem Fortschritt,
// Priorität als Segment (sofort gespeichert über speichereFelder), Kategorie, Dienstleister-Vorschlag,
// Erstellt. Im Bearbeiten-Modus alle Felder wie bisher in TicketEigenschaften (Werte in editData).
import { useState, type Dispatch, type SetStateAction } from 'react';
import { CaretUpDown, SlidersHorizontal, Sparkle, Wrench, X } from '@phosphor-icons/react';
import { Segment } from '../../../../components/ui/Segment';
import { PRIO_REIHENFOLGE, STATUS_REIHENFOLGE, fortschrittIndex } from '../../../../lib/tickets/darstellung';
import { OPTION_STIL, STATUS_ICON, STATUS_ICON_STANDARD, prioFarbe, prioLabel, statusFarbe, statusLabel } from '../liste/teile';
import { WidgetHuelle } from '../../../../components/ui/WidgetHuelle';
import type { Kategorie, Ticket, TicketEditData } from '../types';
import { FELD, LABEL, RundKlein, SuchAuswahl, datumKurz, useEigentuemerVorschlaege } from './widgetTeile';

export interface WidgetStatusProps {
  ticket: Ticket;
  kategorien: Kategorie[];
  teamMembers: { id: string; full_name: string }[];
  teams: { id: string; name: string }[];
  namen: { ersteller: string; dienstleisterVorschlag: string | null; bearbeiterVorschlag: string | null };
  bearbeiten: boolean;
  editData: TicketEditData;
  setEditData: Dispatch<SetStateAction<TicketEditData>>;
  /** Statuswechsel inkl. Abschluss-Prüfung */
  onStatus: (status: string) => void;
  /** Feld sofort speichern (gleicher Schreibweg wie Speichern) */
  onSpeichern: (teil: Partial<TicketEditData>) => void;
}

export function WidgetStatus({ ticket, kategorien, teamMembers, teams, namen, bearbeiten, editData, setEditData, onStatus, onSpeichern }: WidgetStatusProps) {
  const farbe = statusFarbe(ticket.status);
  const SI = STATUS_ICON[ticket.status] ?? STATUS_ICON_STANDARD;
  const schritt = fortschrittIndex(ticket.status);

  // Objekt/Mieter im Bearbeiten-Modus: angezeigter Name der neuen Auswahl
  const [objektLabel, setObjektLabel] = useState(ticket.objekt?.bezeichnung ?? '');
  const [mieterLabel, setMieterLabel] = useState(ticket.mieter_rel ? `${ticket.mieter_rel.vorname} ${ticket.mieter_rel.nachname}` : '');
  const eigRel = ticket.eigentuemer_rel;
  const [eigentuemerLabel, setEigentuemerLabel] = useState(eigRel ? ((eigRel.firma_name ?? '').trim() || `${eigRel.vorname ?? ''} ${eigRel.nachname ?? ''}`.trim()) : '');
  const eigVorschlaege = useEigentuemerVorschlaege(editData.objekt_id || null, ticket.einheit_id, bearbeiten && !editData.eigentuemer_id);

  const kat = ticket.kategorie?.name;
  const unterKat = ticket.unterkategorie_id ? kategorien.find((k) => k.id === ticket.unterkategorie_id)?.name : undefined;
  const prio = bearbeiten ? editData.prioritaet : ticket.prioritaet;
  const zeigeVorschlag = !!ticket.bearbeiter_vorschlag_id && ticket.bearbeiter_vorschlag_id !== (bearbeiten ? editData.zustaendig_id : ticket.zustaendig_id);

  return (
    <WidgetHuelle titel="Status & Eigenschaften" icon={SlidersHorizontal} farbe={farbe} wasserzeichen={SI} schein={farbe} style={{ flexShrink: 0 }}>
      {/* Status-Zeile: natives Auswahlfeld liegt unsichtbar darüber (Tastatur + Mobil) */}
      <div className="immo-zeile-hover relative flex items-center"
        data-tip="Status – Klicken und neuen Status wählen. Erledigt und Geschlossen fragen nach dem Abschlussgrund."
        style={{ gap: 10, padding: 8, margin: '0 -8px', borderRadius: 16 }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 36, height: 36, borderRadius: 11, background: farbe, color: '#fff', boxShadow: `0 6px 14px -6px ${farbe}` }}>
          <SI size={19} weight="fill" />
        </span>
        <span className="min-w-0 flex-1 truncate" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.025em' }}>{statusLabel(ticket.status)}</span>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 30, height: 30, borderRadius: '50%', background: 'var(--fill2)', color: 'var(--ink2)' }}>
          <CaretUpDown size={14} weight="bold" />
        </span>
        <select value={ticket.status} onChange={(e) => onStatus(e.target.value)} aria-label="Status ändern"
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0" style={{ fontFamily: 'inherit' }}>
          {STATUS_REIHENFOLGE.map((s) => <option style={OPTION_STIL} key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
      </div>
      <div className="flex" style={{ gap: 4 }} aria-hidden>
        {STATUS_REIHENFOLGE.map((s, i) => (
          <span key={s} style={{ flex: 1, height: 5, borderRadius: 3, background: i <= schritt ? farbe : 'var(--fill2)', opacity: i <= schritt ? 1 : 0.9 }} />
        ))}
      </div>
      <div style={{ height: 1, background: 'var(--line)' }} />

      {bearbeiten && (
        <label className="flex flex-col" style={{ gap: 6 }}>
          <span style={LABEL}>Zuständig</span>
          <select value={editData.zustaendig_id} data-tip="Zuständig – Mitarbeiter oder Team, das sich kümmert" style={FELD}
            onChange={(e) => {
              const id = e.target.value;
              const typ: 'mitarbeiter' | 'team' | null = id ? (teams.some((t) => t.id === id) ? 'team' : 'mitarbeiter') : null;
              setEditData({ ...editData, zustaendig_id: id, zustaendig_typ: typ });
            }}>
            <option style={OPTION_STIL} value="">– Niemand –</option>
            <optgroup style={OPTION_STIL} label="Mitarbeiter">{teamMembers.map((m) => <option style={OPTION_STIL} key={m.id} value={m.id}>{m.full_name}</option>)}</optgroup>
            {teams.length > 0 && <optgroup style={OPTION_STIL} label="Teams">{teams.map((t) => <option style={OPTION_STIL} key={t.id} value={t.id}>{t.name}</option>)}</optgroup>}
          </select>
          {zeigeVorschlag && (
            <span className="flex items-center" style={{ gap: 8, padding: '8px 10px', borderRadius: 12, background: 'var(--acc-soft)' }}>
              <Sparkle size={14} weight="fill" color="var(--acc)" />
              <span className="min-w-0 flex-1 truncate" style={{ fontSize: 12 }}><span style={{ color: 'var(--ink3)' }}>Empfohlener Bearbeiter</span> <b>{namen.bearbeiterVorschlag || '…'}</b></span>
              <button type="button" data-tip="Übernehmen – Empfohlenen Bearbeiter zuweisen"
                onClick={() => setEditData({ ...editData, zustaendig_id: ticket.bearbeiter_vorschlag_id!, zustaendig_typ: ticket.bearbeiter_vorschlag_typ === 'team' ? 'team' : 'mitarbeiter' })}
                style={{ height: 26, padding: '0 10px', borderRadius: 13, border: 'none', cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)', fontFamily: 'inherit', fontSize: 11, fontWeight: 800 }}>
                Übernehmen
              </button>
            </span>
          )}
        </label>
      )}

      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Priorität</span>
        <Segment ariaLabel="Priorität" voll hoehe={34} wert={prio}
          onChange={(p) => { if (bearbeiten) setEditData({ ...editData, prioritaet: p }); else if (p !== ticket.prioritaet) onSpeichern({ prioritaet: p }); }}
          eintraege={PRIO_REIHENFOLGE.map((p) => ({ key: p, tip: `${prioLabel(p)} – Priorität setzen`,
            label: <span className="inline-flex items-center" style={{ gap: 6 }}><span aria-hidden style={{ width: 6, height: 6, borderRadius: '50%', background: prioFarbe(p) }} />{prioLabel(p)}</span> }))} />
      </div>

      {bearbeiten && (
        <>
          <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
            <label className="flex min-w-0 flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Fällig</span>
              <input type="date" value={editData.faellig_am} onChange={(e) => setEditData({ ...editData, faellig_am: e.target.value })}
                data-tip="Fällig am – Bis wann das Ticket erledigt sein soll" style={FELD} />
            </label>
            <label className="flex min-w-0 flex-col" style={{ gap: 6 }}>
              <span style={LABEL}>Wiedervorlage</span>
              <input type="date" value={editData.wiedervorlage_am} onChange={(e) => setEditData({ ...editData, wiedervorlage_am: e.target.value })}
                data-tip="Wiedervorlage – An diesem Tag wieder vorlegen" style={FELD} />
            </label>
          </div>
          <label className="flex flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Wiedervorlage-Notiz</span>
            <input value={editData.wiedervorlage_notiz} onChange={(e) => setEditData({ ...editData, wiedervorlage_notiz: e.target.value })}
              placeholder="Notiz (optional)" data-tip="Notiz zur Wiedervorlage" style={FELD} />
          </label>
        </>
      )}

      <div className="flex flex-col" style={{ gap: 6 }}>
        <span style={LABEL}>Kategorie</span>
        {bearbeiten ? (
          <select value={editData.kategorie_id} onChange={(e) => setEditData({ ...editData, kategorie_id: e.target.value })}
            data-tip="Kategorie – Art des Schadens oder der Aufgabe" style={FELD}>
            <option style={OPTION_STIL} value="">– Keine –</option>
            {kategorien.map((k) => <option style={OPTION_STIL} key={k.id} value={k.id}>{k.parent_id ? '↳ ' : ''}{k.name}</option>)}
          </select>
        ) : (
          <span className="flex min-w-0 items-center" style={{ gap: 8, fontSize: 15, fontWeight: 800, color: kat ? 'var(--ink)' : 'var(--ink3)' }}>
            {kat && <span aria-hidden style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0, background: ticket.kategorie?.farbe || 'var(--info)' }} />}
            <span className="truncate">{kat ? (unterKat ? `${kat} › ${unterKat}` : kat) : '–'}</span>
          </span>
        )}
      </div>

      {bearbeiten && (
        <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))' }}>
          <div className="flex min-w-0 flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Objekt</span>
            {editData.objekt_id ? (
              <span className="flex min-w-0 items-center" style={{ ...FELD, gap: 6, paddingRight: 6 }}>
                <span className="min-w-0 flex-1 truncate">{objektLabel || '—'}</span>
                <RundKlein icon={X} groesse={26} label="Objekt entfernen" onClick={() => { setEditData({ ...editData, objekt_id: '' }); setObjektLabel(''); }} />
              </span>
            ) : (
              <SuchAuswahl art="objekt" onWahl={(t) => { setEditData({ ...editData, objekt_id: t.id }); setObjektLabel(t.titel); }} />
            )}
          </div>
          <div className="flex min-w-0 flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Mieter</span>
            {editData.mieter_id ? (
              <span className="flex min-w-0 items-center" style={{ ...FELD, gap: 6, paddingRight: 6 }}>
                <span className="min-w-0 flex-1 truncate">{mieterLabel || '—'}</span>
                <RundKlein icon={X} groesse={26} label="Entfernen" tip="Entfernen – Mieter vom Ticket lösen" onClick={() => { setEditData({ ...editData, mieter_id: '' }); setMieterLabel(''); }} />
              </span>
            ) : (
              <SuchAuswahl art="mieter" onWahl={(t) => { setEditData({ ...editData, mieter_id: t.id }); setMieterLabel(t.titel); }} />
            )}
          </div>
          <div className="flex min-w-0 flex-col" style={{ gap: 6 }}>
            <span style={LABEL}>Eigentümer</span>
            {editData.eigentuemer_id ? (
              <span className="flex min-w-0 items-center" style={{ ...FELD, gap: 6, paddingRight: 6 }}>
                <span className="min-w-0 flex-1 truncate">{eigentuemerLabel || '—'}</span>
                <RundKlein icon={X} groesse={26} label="Entfernen" tip="Entfernen – Eigentümer vom Ticket lösen" onClick={() => { setEditData({ ...editData, eigentuemer_id: '' }); setEigentuemerLabel(''); }} />
              </span>
            ) : (
              <SuchAuswahl art="eigentuemer" vorschlaege={eigVorschlaege} onWahl={(t) => { setEditData({ ...editData, eigentuemer_id: t.id }); setEigentuemerLabel(t.titel); }} />
            )}
          </div>
        </div>
      )}

      {ticket.dienstleister_vorschlag_id && (
        <span className="flex min-w-0 items-center" data-tip="Dienstleister-Vorschlag – Passender Fachbetrieb für diese Kategorie"
          style={{ gap: 8, padding: '9px 12px', borderRadius: 12, background: 'var(--fill)' }}>
          <Wrench size={14} weight="fill" color="var(--warn)" className="shrink-0" />
          <span className="min-w-0 truncate" style={{ fontSize: 12.5 }}><span style={{ color: 'var(--ink3)' }}>Dienstleister-Vorschlag</span> <b>{namen.dienstleisterVorschlag || '…'}</b></span>
        </span>
      )}

      <div className="flex items-center justify-between" style={{ gap: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink3)' }}>Erstellt</span>
        <span className="truncate" style={{ fontSize: 13, fontWeight: 800 }}>
          {datumKurz(ticket.created_at)}
          {namen.ersteller ? ` · ${namen.ersteller}` : ticket.quelle_email_id ? ' · aus E-Mail' : ''}
        </span>
      </div>
    </WidgetHuelle>
  );
}
